// src/components/TerminatedEmployees.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  RestoreFromTrash as RestoreIcon
} from '@mui/icons-material';
import { collection, query, orderBy, onSnapshot, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { tryResolveTenantId } from '../utils/tenant';
import { canAccessFeature } from '../utils/permissions';

export default function TerminatedEmployees() {
  const [terminatedEmployees, setTerminatedEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [userPermission, setUserPermission] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load user permissions
  useEffect(() => {
    const adminUser = localStorage.getItem('adminUser');
    if (adminUser) {
      try {
        const user = JSON.parse(adminUser);
        setUserPermission(user.permissionLevel);
      } catch (err) {
        console.error('Error parsing user data:', err);
      }
    }
  }, []);

  // Load terminated employees
  useEffect(() => {
    const setup = async () => {
      const tenantId = await tryResolveTenantId();
      if (!tenantId) {
        console.warn('TerminatedEmployees: tenantId not resolved');
        setLoading(false);
        return () => {};
      }

      const q = query(collection(db, `tenants/${tenantId}/terminations`), orderBy('createdAt', 'desc'));
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
      const terminated = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTerminatedEmployees(terminated);
      setFilteredEmployees(terminated);
      setLoading(false);
    });
      return () => unsubscribe();
    };

    const unsubscribeFn = setup();
    return () => { if (unsubscribeFn && typeof unsubscribeFn === 'function') unsubscribeFn(); };
  }, []);

  // Filter employees based on search
  useEffect(() => {
    if (!searchQuery) {
      setFilteredEmployees(terminatedEmployees);
    } else {
      const filtered = terminatedEmployees.filter(emp =>
        emp.employeeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        emp.employeeEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
        emp.reason.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredEmployees(filtered);
    }
  }, [searchQuery, terminatedEmployees]);

  const handleViewDetails = (employee) => {
    setSelectedEmployee(employee);
    setDetailsOpen(true);
  };

  const handleDeleteConfirm = (employee) => {
    setEmployeeToDelete(employee);
    setDeleteConfirmOpen(true);
  };

  const handlePermanentDelete = async () => {
    if (!employeeToDelete) return;

    try {
  // Delete from terminations archive
  const tenantId = await tryResolveTenantId();
  if (!tenantId) throw new Error('tenantId not resolved');
  await deleteDoc(doc(db, `tenants/${tenantId}/terminations`, employeeToDelete.id));
      
  // Delete employee record completely
  await deleteDoc(doc(db, `tenants/${tenantId}/employees`, employeeToDelete.employeeId));
      
      setDeleteConfirmOpen(false);
      setEmployeeToDelete(null);
    } catch (err) {
      console.error('Error permanently deleting employee:', err);
    }
  };

  const handleRestore = async (employee) => {
    try {
      // Update employee status back to active
  const tenantId = await tryResolveTenantId();
  if (!tenantId) throw new Error('tenantId not resolved');
  const employeeRef = doc(db, `tenants/${tenantId}/employees`, employee.employeeId);
  await updateDoc(employeeRef, {
        'employment.currentStatus': 'Active',
        'employment.terminationDate': null,
        'employment.terminationReason': null,
        'employment.isRehireable': null,
        'employment.terminatedBy': null,
        'employment.terminationNotes': null
      });

  // Remove from terminations archive
  await deleteDoc(doc(db, `tenants/${tenantId}/terminations`, employee.id));
    } catch (err) {
      console.error('Error restoring employee:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const isOwner = canAccessFeature(userPermission, 'settings_central');
  const totalArchived = terminatedEmployees.length;
  const rehireableCount = terminatedEmployees.filter((emp) => emp.isRehireable).length;
  const notRehireableCount = totalArchived - rehireableCount;

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(145deg, #f8fafc 0%, #eef2f7 100%)'
          }}
        >
          <Typography variant="h6">Loading terminated employees...</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        background: 'linear-gradient(180deg, #f9fbfd 0%, #f2f5f9 100%)',
        minHeight: '100%'
      }}
    >
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(135deg, #ffffff 0%, #f6f9fd 100%)'
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em', mb: 2 }}>
          Terminated Employees Archive
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Chip
            label={`Total Archived: ${totalArchived}`}
            color="default"
            sx={{ borderRadius: 1.5, fontWeight: 600, bgcolor: '#eef2f7' }}
          />
          <Chip
            label={`Rehireable: ${rehireableCount}`}
            color="success"
            sx={{ borderRadius: 1.5, fontWeight: 600 }}
          />
          <Chip
            label={`Not Rehireable: ${notRehireableCount}`}
            color="error"
            sx={{ borderRadius: 1.5, fontWeight: 600 }}
          />
        </Stack>
      </Paper>

      {/* Search */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: '#fff'
        }}
      >
        <TextField
          fullWidth
          placeholder="Search by name, email, or termination reason..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            sx: {
              borderRadius: 2,
              backgroundColor: '#fbfcff'
            }
          }}
        />
      </Paper>

      {/* Results */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          backgroundColor: '#fff'
        }}
      >
        <TableContainer sx={{ maxHeight: '65vh' }}>
          <Table sx={{ minWidth: 980 }}>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: '#f3f6fb',
                  '& .MuiTableCell-root': {
                    borderBottomColor: 'divider',
                    color: 'text.secondary',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '0.76rem',
                    letterSpacing: '0.06em',
                    py: 1.8
                  }
                }}
              >
                <TableCell>Employee Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Termination Date</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Rehireable</TableCell>
                <TableCell>Terminated By</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEmployees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                      {searchQuery ? 'No terminated employees found matching your search.' : 'No terminated employees in archive.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredEmployees.map((employee, index) => (
                  <TableRow
                    key={employee.id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#fbfcfe',
                      '&:hover': { backgroundColor: '#f1f6fd' },
                      transition: 'background-color 120ms ease-in-out'
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{employee.employeeName}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{employee.employeeEmail}</TableCell>
                    <TableCell>{formatDate(employee.terminationDate)}</TableCell>
                    <TableCell sx={{ maxWidth: 240 }}>
                      <Typography noWrap title={employee.reason}>
                        {employee.reason}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={employee.isRehireable ? 'Yes' : 'No'}
                        color={employee.isRehireable ? 'success' : 'error'}
                        size="small"
                        sx={{ borderRadius: 1.5, fontWeight: 700, minWidth: 56 }}
                      />
                    </TableCell>
                    <TableCell>{employee.terminatedBy}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          onClick={() => handleViewDetails(employee)}
                          sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)', mr: 0.5, '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.18)' } }}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      
                      {isOwner && (
                        <>
                          <Tooltip title="Restore Employee">
                            <IconButton 
                              onClick={() => handleRestore(employee)}
                              color="info"
                              sx={{ mr: 0.5, bgcolor: 'rgba(2, 136, 209, 0.1)', '&:hover': { bgcolor: 'rgba(2, 136, 209, 0.2)' } }}
                            >
                              <RestoreIcon />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Permanently Delete">
                            <IconButton 
                              onClick={() => handleDeleteConfirm(employee)}
                              color="error"
                              sx={{ bgcolor: 'rgba(211, 47, 47, 0.1)', '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.2)' } }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Termination Details: {selectedEmployee?.employeeName}
        </DialogTitle>
        <DialogContent>
          {selectedEmployee && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography><strong>Email:</strong> {selectedEmployee.employeeEmail}</Typography>
              <Typography><strong>Termination Date:</strong> {formatDate(selectedEmployee.terminationDate)}</Typography>
              <Typography><strong>Reason:</strong> {selectedEmployee.reason}</Typography>
              <Typography><strong>Terminated By:</strong> {selectedEmployee.terminatedBy}</Typography>
              <Typography><strong>Terminated By User:</strong> {selectedEmployee.terminatedByUser}</Typography>
              <Typography>
                <strong>Rehireable:</strong> 
                <Chip
                  label={selectedEmployee.isRehireable ? 'Yes' : 'No'}
                  color={selectedEmployee.isRehireable ? 'success' : 'error'}
                  size="small"
                  sx={{ ml: 1 }}
                />
              </Typography>
              {selectedEmployee.notes && (
                <Typography><strong>Notes:</strong> {selectedEmployee.notes}</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Permanently Delete Employee</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography>
            Are you sure you want to permanently delete <strong>{employeeToDelete?.employeeName}</strong> 
            from the system? This will remove all records including the termination archive.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handlePermanentDelete} color="error" variant="contained">
            Permanently Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
